import { MarkEmailReadOutlined } from '@mui/icons-material'
import { Box, Button, CircularProgress, Typography } from '@mui/material'
import React, { useEffect, useRef, useState } from 'react'
import SingleDigitInput from './SingleDigitInput';

const CODE_LENGTH = 6;
const emptyCode = () => Array(CODE_LENGTH).fill("");

const OtpPopup = ({
  visible,
  setVisible,
  submitOtp,
  errorMessage,
  email,
  loading,
}) => {

  const digitRefs = useRef([]);
  const [digits, setDigits] = useState(emptyCode);

  const isComplete = digits.every(d => d !== "");

  const _submitOtp = () => {
    if (isComplete && !loading) {
      submitOtp(digits.join(""));
    }
  }

  const setDigit = (index, value) => {
    setDigits(prev => prev.map((d, i) => (i === index ? value : d)));
  }

  const focusDigit = (index) => {
    const input = digitRefs.current[index];
    if (input) {
      input.focus();
      input.select();
    }
  }

  const goToNextDigit = (index) => {
    if (index < CODE_LENGTH - 1) focusDigit(index + 1);
  }

  // Backspace on an empty box clears the previous one; arrow keys only move
  const goToPrevDigit = (index, clear = true) => {
    if (index > 0) {
      if (clear) setDigit(index - 1, "");
      focusDigit(index - 1);
    }
  }

  // Paste a whole code (e.g. "123456" or "123 456") into the boxes
  const handlePaste = (event) => {
    const pasted = event.clipboardData.getData("text").replace(/\D/g, "").slice(0, CODE_LENGTH);
    if (!pasted) return;
    event.preventDefault();
    setDigits(emptyCode().map((_, i) => pasted[i] ?? ""));
    focusDigit(Math.min(pasted.length, CODE_LENGTH - 1));
  }

  function cancel() {
    setVisible(false);
  }

  useEffect(() => {
    if (visible) {
      focusDigit(0);
    } else {
      setDigits(emptyCode());
    }
  }, [visible]);

  // Submit automatically once all digits are entered
  useEffect(() => {
    _submitOtp();
  }, [digits]);

  // Close on Escape
  useEffect(() => {
    if (!visible) return;
    const onKey = (event) => { if (event.key === "Escape") cancel(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [visible]);

  if (!visible) return null;

  return (
    // Overlay
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
        backgroundColor: "rgba(10, 15, 28, 0.55)",
        backdropFilter: "blur(4px)",
        zIndex: 1000,
      }}
      onClick={cancel}
    >
      {/* Content */}
      <Box
        role="dialog"
        aria-modal="true"
        aria-labelledby="otp-title"
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          width: "100%",
          maxWidth: 440,
          boxSizing: "border-box",
          backgroundColor: "#FFFFFF",
          p: { xs: 3, sm: 4 },
          borderRadius: "16px",
          boxShadow: "0 24px 48px rgba(15, 23, 42, 0.2)",
          textAlign: "center",
        }}
        onClick={(event) => event.stopPropagation()}
      >
        <Box sx={{
          width: 56,
          height: 56,
          borderRadius: "16px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#EFF6FF",
          color: "#137FEC",
          mb: 2,
        }}>
          <MarkEmailReadOutlined sx={{ fontSize: 28 }} />
        </Box>

        <Typography id="otp-title" sx={{ fontSize: 22, fontWeight: 700, color: "#0F172A", letterSpacing: "-0.01em" }}>
          Check your email
        </Typography>

        <Typography sx={{ fontSize: 14, color: "#64748B", mt: 1, lineHeight: 1.5 }}>
          We sent a 6-digit verification code to
          <br />
          <Box component="span" sx={{ fontWeight: 600, color: "#334155" }}>{email}</Box>
        </Typography>

        <Box sx={{ display: "flex", gap: { xs: 1, sm: 1.25 }, mt: 3.5 }}>
          {digits.map((digit, index) => (
            <SingleDigitInput
              key={index}
              id={`digit${index + 1}`}
              index={index}
              value={digit}
              setValue={(value) => setDigit(index, value)}
              digitRef={(el) => { digitRefs.current[index] = el; }}
              next={goToNextDigit}
              prev={goToPrevDigit}
              enter={_submitOtp}
              onPaste={handlePaste}
              hasError={Boolean(errorMessage)}
            />
          ))}
        </Box>

        <Typography role="alert" sx={{ minHeight: 22, fontSize: 14, color: "#B91C1C", mt: 1.5 }}>
          {errorMessage}
        </Typography>

        <Button
          variant="contained"
          disableElevation
          disabled={!isComplete || loading}
          onClick={_submitOtp}
          sx={{
            width: "100%",
            height: 48,
            mt: 1.5,
            borderRadius: "10px",
            fontSize: 15,
            fontWeight: 600,
            textTransform: "none",
            backgroundColor: "#137FEC",
            "&:hover": { backgroundColor: "#0F6FD1" },
          }}
        >
          {loading
            ? <CircularProgress size={22} sx={{ color: 'white' }} />
            : "Verify"
          }
        </Button>

        <Button
          onClick={cancel}
          sx={{ mt: 1, textTransform: "none", color: "#64748B", fontWeight: 500 }}
        >
          Back to sign in
        </Button>
      </Box>
    </Box>
  )
}

export default OtpPopup
